import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket, faCheck, faCopy } from '@fortawesome/free-solid-svg-icons';
import { copyToClipboard } from 'utils/copyToClipboard';
import { inviteUrl, inviteUrlDisplay } from 'utils/play/inviteUrl';
import './ShareLinkCard.css';

interface Props {
  code: string;
  tournamentName: string;
}

/** The Copied state lasts this long, matching the prototype's 1.6 s. */
const COPIED_MS = 1600;

const canShare = (): boolean => typeof navigator !== 'undefined' && typeof navigator.share === 'function';

/**
 * Decision 3, design review item 4: the INVITE PLAYERS card at the top of the
 * lobby. Link left, 72px QR right, then Copy link (#534cd4, turns into a check
 * plus Copied for 1.6 s) and Share (#323232, native share sheet). Without
 * navigator.share the second slot is a Copy link button (Eric's ruling 3).
 */
export const ShareLinkCard: React.FC<Props> = ({ code, tournamentName }) => {
  const url = inviteUrl(code);
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url, { errorCorrectionLevel: 'M', width: 288, margin: 1, color: { dark: '#000000', light: '#ffffff' } })
      .then((dataUrl) => {
        if (!cancelled) setQr(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setQr(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const copy = async () => {
    const ok = await copyToClipboard(url);
    setCopied(ok);
    setCopyFailed(!ok);
    window.setTimeout(() => {
      setCopied(false);
      setCopyFailed(false);
    }, COPIED_MS);
  };

  const share = async () => {
    try {
      await navigator.share({ title: tournamentName, text: `Join ${tournamentName} on TradeMagic Play`, url });
    } catch (err) {
      if ((err as { name?: string })?.name !== 'AbortError') setCopyFailed(true);
    }
  };

  return (
    <section className="play-share" data-testid="play-share-card">
      <div className="play-share__row">
        <div className="play-share__text">
          <p className="play-share__eyebrow">Invite players</p>
          <code className="play-share__url" data-testid="play-invite-url">
            {inviteUrlDisplay(code)}
          </code>
        </div>
        {qr && <img src={qr} alt={`QR code for ${inviteUrlDisplay(code)}`} className="play-share__qr" data-testid="play-invite-qr" />}
      </div>
      <div className="play-share__actions">
        <button type="button" className={`play-share__button play-share__button--copy ${copied ? 'play-share__button--copied' : ''}`} onClick={copy} data-testid="play-copy-link">
          <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
          {copied ? 'Copied' : 'Copy link'}
        </button>
        {canShare() ? (
          <button type="button" className="play-share__button play-share__button--share" onClick={share} data-testid="play-share-link">
            <FontAwesomeIcon icon={faArrowUpFromBracket} />
            Share
          </button>
        ) : (
          <button type="button" className="play-share__button play-share__button--share" onClick={copy} data-testid="play-share-fallback">
            <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
            {copied ? 'Copied' : 'Copy link'}
          </button>
        )}
      </div>
      {copyFailed && (
        <p className="play-error" role="alert">
          Couldn't copy. Long-press the link to copy it.
        </p>
      )}
    </section>
  );
};

export default ShareLinkCard;
